import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import useGetLoans from '../../hooks/useGetLoans'
import DotsLoading from 'components/loadings/dotsLoading'
import { PulseCard } from 'components/states'
import Item from './item'
import useDeleteLoan from '../../hooks/useDeleteLoan'
import { useCallback, useEffect, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import posthog from 'posthog-js'
import { getLoanCandidates } from 'services/fund'
import { faPlain } from 'utilities/helper/format'
import SortPill from 'components/sortPill'
import { TDemandSort } from 'services/loans'
import {
    DEMAND_PRIORITIES,
    demandPriority,
} from 'utilities/helper/demandPriority'

/**
 * The orders a manager reads the queue in.
 *
 * Four answer a question the server can order the whole queue by:
 *
 * - «قدیمی‌ترین درخواست» — who has waited longest. The default: in a
 *   قرض‌الحسنه fund the fair place to start reading is the front of the line.
 * - «جدیدترین» — who has just asked.
 * - «فوریت» — the فوریت the member picked, blended with how long they have
 *   waited. The server lifts each request from the day its window opens
 *   (filed + 0 / 3 / 10 / 30 days), so an old «پایین» is not buried forever
 *   under every new «فوری». The owner asked for exactly that blend.
 * - «بیشترین موافقت مدیران» — managers' agreements, fewest standing
 *   objections next. The server only honours it for a manager.
 *
 * The fifth, رتبهٔ اعتباری, is the fund-wide ranking the grant wizard sorts
 * by, and it is applied here over the whole queue (see `stillLoadingQueue`).
 */
type TSort = TDemandSort | 'rank'

const SORTS: { key: TSort; label: string }[] = [
    { key: 'oldest', label: 'قدیمی‌ترین درخواست' },
    { key: 'newest', label: 'جدیدترین' },
    { key: 'rank', label: 'رتبهٔ اعتباری' },
    { key: 'urgency', label: 'فوریت' },
    { key: 'agreement', label: 'بیشترین موافقت مدیران' },
]

/**
 * The فوریت the member picked when they filed, as a filter.
 *
 * Not «چند روز پیش ثبت شده» — how long a request has waited is the fund's
 * doing, not the member's, and filtering by it hides exactly the people the
 * queue has already failed. What the member actually said about their own
 * need is the one thing the request form asks, and that is this.
 */
const PRIORITY_STEPS = [
    { value: '', label: 'همه', aria: 'درخواست‌ها با هر فوریتی' },
    ...DEMAND_PRIORITIES.map((key) => {
        const meta = demandPriority(key)!
        return {
            value: key as string,
            label: meta.label,
            aria: `فقط فوریت ${meta.label}`,
        }
    }),
]

/** Never pull more than the ranking itself covers (the server caps at 600). */
const MAX_PAGES = 20

export default function Requested({
    isManager: isManagerProp,
}: {
    /** from the page, which reads the role off the fund; router state is the fallback */
    isManager?: boolean
} = {}) {
    const { id } = useParams()
    const { state } = useLocation()
    const isManager = isManagerProp ?? !!state?.isManager
    const [sort, setSort] = useState<TSort>('oldest')
    const [priority, setPriority] = useState('')
    const {
        demandLoans,
        isLoadingDemandLoans,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        // the rank order is laid over the queue in its default order
    } = useGetLoans(id, sort === 'rank' ? 'oldest' : sort)
    const { isLoading, mutate } = useDeleteLoan(refetch)
    const onWatchEnd = useCallback(() => {
        fetchNextPage()
    }, [fetchNextPage])

    /**
     * The ranking, fetched only when the manager actually asks to sort by it.
     *
     * A rank is a position, so producing one means ranking the whole fund —
     * and this is the screen a manager opens constantly. Paying for that scan
     * on every visit, to power an order most visits do not use, is the wrong
     * trade. `requests_only` narrows it to exactly the population of this tab,
     * and the query key is the one the card's own rank row already uses, so
     * opening a card afterwards costs nothing more.
     */
    const candidates = useQuery(
        ['loan-candidates', id, true, false],
        getLoanCandidates,
        {
            staleTime: 5 * 60 * 1000,
            enabled: !!id && isManager && sort === 'rank',
        }
    )
    const rankOf = useMemo(() => {
        const map = new Map<number, number>()
        candidates.data?.results?.forEach((row) =>
            map.set(row.poolship_id, row.rank)
        )
        return map
    }, [candidates.data])

    const loaded = useMemo(
        () => demandLoans?.pages.flatMap((page) => page.results) ?? [],
        [demandLoans]
    )
    const pagesLoaded = demandLoans?.pages.length ?? 0

    /**
     * Sorting a paginated list by something the page order knows nothing
     * about is only honest once the whole list is here — otherwise «بالاترین
     * رتبه» means «بالاترین رتبه از میان آنهایی که تا حالا اسکرول کرده‌ای»,
     * which is a different and useless claim. So the rank order pulls the
     * rest of the queue in behind it.
     */
    const stillLoadingQueue =
        sort === 'rank' && !!hasNextPage && pagesLoaded < MAX_PAGES
    useEffect(() => {
        if (stillLoadingQueue && !isFetchingNextPage) fetchNextPage()
    }, [stillLoadingQueue, isFetchingNextPage, fetchNextPage])

    const picked = useMemo(
        () =>
            priority
                ? loaded.filter((row) => row.demand_priority === priority)
                : loaded,
        [loaded, priority]
    )
    const hiddenByPriority = loaded.length - picked.length

    const rows = useMemo(() => {
        if (sort !== 'rank' || rankOf.size === 0) return picked
        // a request whose member has no ranked row (deactivated, checked out)
        // sorts last rather than first — Infinity, not 0
        const rank = (row: IDemandResult) =>
            rankOf.get(row.poolship?.id) ?? Number.POSITIVE_INFINITY
        return [...picked].sort((a, b) => rank(a) - rank(b))
    }, [picked, sort, rankOf])

    const chooseSort = (next: TSort) => {
        setSort(next)
        posthog.capture('loan_demands_sorted', {
            fund_id: id,
            sort: next,
            row_count: loaded.length,
        })
    }

    const total = demandLoans?.pages?.[0]?.count ?? loaded.length

    return (
        <div className=" pb-6">
            {/* Managers only: the ranking endpoint is the manager's worklist,
                and a member sorting other members by credit rank is not a
                thing this screen should offer. */}
            {isManager && (
                <div className=" mt-2 rounded-2xl border border-stroke bg-white p-3">
                    <p className=" mb-1.5 text-[11px] text-text-muted">ترتیب</p>
                    <div
                        role="radiogroup"
                        aria-label="ترتیب فهرست"
                        className=" flex flex-wrap items-center gap-2"
                    >
                        {SORTS.map((option) => (
                            <SortPill
                                key={option.key}
                                checked={sort === option.key}
                                onChange={() => chooseSort(option.key)}
                                label={option.label}
                            />
                        ))}
                    </div>
                    {sort === 'urgency' && (
                        <p className=" mt-2 text-[11px] leading-5 text-text-secondary">
                            فوریت و مدت انتظار با هم حساب می‌شود: هر درخواست از
                            روزی جلو می‌افتد که عضو گفته پول را لازم دارد؛ فوری
                            از روز ثبت، بالا ۳ روز بعد، متوسط ۱۰ روز بعد و پایین
                            ۳۰ روز بعد.
                        </p>
                    )}
                    <p className=" mb-1.5 mt-3 text-[11px] text-text-muted">
                        فوریت
                    </p>
                    <div
                        role="radiogroup"
                        aria-label="فیلتر فوریت"
                        className=" flex flex-wrap items-center gap-2"
                    >
                        {PRIORITY_STEPS.map((step) => (
                            <SortPill
                                key={step.value}
                                checked={priority === step.value}
                                onChange={() => {
                                    setPriority(step.value)
                                    posthog.capture(
                                        'loan_demands_priority_filtered',
                                        {
                                            fund_id: id,
                                            priority: step.value || null,
                                            sort,
                                            row_count: loaded.length,
                                        }
                                    )
                                }}
                                label={step.label}
                                aria={step.aria}
                            />
                        ))}
                    </div>
                </div>
            )}

            {!isLoadingDemandLoans && demandLoans && (
                <p className=" mb-2 mt-3 px-1 text-[11px] text-text-secondary">
                    {sort === 'rank'
                        ? stillLoadingQueue || candidates.isLoading
                            ? 'در حال آوردن رتبهٔ همهٔ درخواست‌ها…'
                            : candidates.isError
                            ? 'رتبه‌ها الان در دسترس نیست؛ فهرست به ترتیب تاریخ مانده.'
                            : `از بالاترین رتبه به پایین — ${faPlain.format(
                                  rows.length
                              )} درخواست`
                        : `${faPlain.format(total)} درخواست در انتظار بررسی`}
                </p>
            )}

            {hiddenByPriority > 0 && (
                <p className=" mb-2 px-1 text-[11px] text-text-muted">
                    {faPlain.format(hiddenByPriority)} درخواست با فوریت دیگر
                    پنهان شده — برای دیدن‌شان فوریت را روی «همه» بگذارید
                </p>
            )}

            {!isLoadingDemandLoans && demandLoans ? (
                <>
                    {rows.length === 0 && (
                        <p className=" mt-3 rounded-2xl border border-stroke bg-white p-6 text-center text-xs leading-6 text-text-secondary">
                            {loaded.length === 0
                                ? 'درخواست وامی در انتظار بررسی نیست.'
                                : 'درخواستی با این فوریت نیست.'}
                        </p>
                    )}
                    <InfiniteScroll
                        next={onWatchEnd}
                        hasMore={sort === 'rank' ? false : !!hasNextPage}
                        dataLength={rows.length}
                        loader={<DotsLoading />}
                        style={{ overflow: 'visible' }}
                    >
                        <div className=" flex flex-col gap-3">
                            {rows.map((item) => (
                                <Item
                                    item={item}
                                    key={item.id}
                                    isLoading={isLoading}
                                    mutate={mutate}
                                    isManager={isManager}
                                    rank={rankOf.get(item.poolship?.id)}
                                />
                            ))}
                        </div>
                    </InfiniteScroll>
                    {stillLoadingQueue && <DotsLoading />}
                </>
            ) : (
                <div className=" mt-3 flex flex-col gap-3">
                    <PulseCard />
                    <PulseCard />
                </div>
            )}
        </div>
    )
}
